/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { CouponTypeCode } from "project-config/constants/activity-code";
import { useLocation } from "react-router-dom";
import { useAnimationTopUpStore } from "../store";
import { useFetchActivityInfoList } from "./useFetchActivityInfoList";
import { BindActivityInfo } from "@/components/top-up/types";
import { AnimationCommonContainer } from "../atom";
import { AnimationBalanceRecharge } from "../components/animation-balance-recharge";
import { QingCloudRecharge } from "../components/qing-cloud-recharge";
import {
  DesktopRechargeTab,
  QingCloudName,
} from "../constants/animation-balance-info";
import { useFetchHangTags } from "./useFetchHangTags";
import DefaultAdvise from "@/assets/images/default-advise.png";
import { DesktopRecharge } from "../components/desktop-recharge";
import { useGlobalStore } from "@/store/GlobalStore";

export function useInitTabList() {
  const { t } = useTranslation("topup");
  const { userInfo } = useGlobalStore();
  useFetchActivityInfoList();
  useFetchHangTags();
  const { setter: setAnimationTopUpStore } = useAnimationTopUpStore();
  const activityInfoList = useAnimationTopUpStore(
    (state) => state.activityInfoList
  );
  const activeName = useAnimationTopUpStore((state) => state.activeName);
  const haveRechargeGiftCouponActivity = useAnimationTopUpStore(
    (state) => state.haveRechargeGiftCouponActivity
  );
  const haveQingCloudActivity = useAnimationTopUpStore(
    (state) => state.haveQingCloudActivity
  );
  const rechargeGiftCouponActivity = useAnimationTopUpStore(
    (state) => state.rechargeGiftCouponActivity
  );
  const qingCloudActivity = useAnimationTopUpStore(
    (state) => state.qingCloudActivity
  );
  const desktopActivity = useAnimationTopUpStore(
    (state) => state.desktopActivity
  );
  const luckDrawGiftActivityInfo = useAnimationTopUpStore(
    (state) => state.luckDrawGiftActivityInfo
  );
  const location = useLocation();

  const rechargeGiftBalanceTabItem = useCallback(
    (activityInfo: BindActivityInfo) => {
      const { imageUrlLink, imageUrlLinkEventDetails } = activityInfo;
      return {
        label: t("topUp_recharge_tab"),
        key: activityInfo.id.toString(),
        advertiseImg: luckDrawGiftActivityInfo
          ? luckDrawGiftActivityInfo.imageUrlLink
          : imageUrlLink || DefaultAdvise,
        advertiseLink: luckDrawGiftActivityInfo
          ? luckDrawGiftActivityInfo.imageUrlLinkEventDetails
          : imageUrlLinkEventDetails || "",
        children: (
          <AnimationCommonContainer>
            <AnimationBalanceRecharge activityInfo={activityInfo} />
          </AnimationCommonContainer>
        ),
      };
    },
    [rechargeGiftCouponActivity, luckDrawGiftActivityInfo]
  );

  const qingCloudTabItem = useCallback(
    (activityInfo: BindActivityInfo) => {
      const { imageUrlLink, imageUrlLinkEventDetails, activityDetail } =
        activityInfo;
      return {
        label:
          activityDetail[0]?.couponType === CouponTypeCode.CPUQingCloud
            ? t("youth_recharge_tab")
            : t("gpu_youth_recharge_tab"),
        key:
          qingCloudActivity().activityDetail[0]?.couponType ===
          CouponTypeCode.CPUQingCloud
            ? QingCloudName.CPUCloud
            : QingCloudName.GPUCloud,
        advertiseImg: imageUrlLink || DefaultAdvise,
        advertiseLink: imageUrlLinkEventDetails || "",
        children: (
          <AnimationCommonContainer>
            <QingCloudRecharge activityInfo={activityInfo} />
          </AnimationCommonContainer>
        ),
      };
    },
    [qingCloudActivity]
  );

  const desktopTabItem = useCallback(
    (activityInfo: BindActivityInfo) => {
      const { imageUrlLink, imageUrlLinkEventDetails } = activityInfo;
      return {
        label: t("desktop_recharge"),
        key: DesktopRechargeTab.Desktop,
        advertiseImg: imageUrlLink || DefaultAdvise,
        advertiseLink: imageUrlLinkEventDetails || "",
        children: (
          <AnimationCommonContainer>
            <DesktopRecharge activityInfo={activityInfo} />
          </AnimationCommonContainer>
        ),
      };
    },
    [desktopActivity()]
  );

  const getTabsList = useCallback(() => {
    const defaultBalanceTabItem = {
      label: t("topUp_recharge_tab"),
      key: "balance-recharge",
      advertiseImg: luckDrawGiftActivityInfo
        ? luckDrawGiftActivityInfo.imageUrlLink
        : DefaultAdvise,
      advertiseLink: luckDrawGiftActivityInfo
        ? luckDrawGiftActivityInfo.imageUrlLinkEventDetails
        : "",
      children: (
        <AnimationCommonContainer>
          <AnimationBalanceRecharge />
        </AnimationCommonContainer>
      ),
    };
    // 动画活动包括 充值赠送券、青云券充值、抽奖券
    // 动画抽奖券和充值赠送可能同时存在
    const tabList = [
      haveRechargeGiftCouponActivity()
        ? rechargeGiftBalanceTabItem(rechargeGiftCouponActivity())
        : defaultBalanceTabItem,
    ];
    if (haveQingCloudActivity()) {
      tabList.push(qingCloudTabItem(qingCloudActivity()));
    }
    if (userInfo?.enableRemoteDesktop) {
      tabList.push(desktopTabItem(desktopActivity()));
    }
    return tabList;
  }, [
    activityInfoList,
    luckDrawGiftActivityInfo,
    activeName,
    location,
    rechargeGiftBalanceTabItem,
    qingCloudTabItem,
    haveRechargeGiftCouponActivity,
    rechargeGiftCouponActivity,
    haveQingCloudActivity,
    qingCloudActivity,
  ]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const activeNameValue = params.get("activeName");
    if (activeNameValue) {
      if (
        activityInfoList.length > 0 &&
        (activityInfoList.find(
          (item) => item.activityName === activeNameValue
        ) ||
          [QingCloudName.CPUCloud, QingCloudName.GPUCloud].includes(
            activeNameValue as any
          ) ||
          (DesktopRechargeTab.Desktop === activeNameValue &&
            userInfo?.enableRemoteDesktop))
      ) {
        setAnimationTopUpStore((state) => {
          state.activeName = activeNameValue;
        });
      } else {
        setAnimationTopUpStore((state) => {
          state.activeName = getTabsList()[0].key;
        });
      }
    } else {
      setAnimationTopUpStore((state) => {
        state.activeName = getTabsList()[0].key;
      });
    }
  }, [activityInfoList, location, activeName]);

  return {
    getTabsList,
  };
}
